import { useMutation, useQueryClient } from '@tanstack/react-query'
import { usePmSigner } from './usePmSigner'
import { usePmWalletStore } from '@/store/pmWalletStore'
import { toast } from 'sonner'
import { hapticSuccess, hapticError } from '@/telegram'
import { redeemConditionViaRelayer } from '@/lib/relayerRedeem'
import type { RedeemablePosition } from '@/lib/polymarketApi'

/** PM redeem via Builder Relayer (client-side, gasless). */
export function useRedeem() {
  const queryClient = useQueryClient()
  const activeWallet = usePmWalletStore((s) => s.activeWallet)
  const pmWallet = usePmSigner()

  return useMutation({
    mutationFn: async (positions: RedeemablePosition[]) => {
      const seen = new Set<string>()
      const unique = positions.filter((p) => {
        if (!p.conditionId || seen.has(p.conditionId)) return false
        seen.add(p.conditionId)
        return true
      })
      if (unique.length === 0) throw new Error('No conditions to redeem')

      if (activeWallet === 'pm' && pmWallet?.signer) {
        for (const pos of unique) {
          const resp = await redeemConditionViaRelayer(pmWallet.signer, {
            conditionId: pos.conditionId,
            negRisk: pos.negRisk,
            size: pos.size,
            outcomeIndex: pos.outcomeIndex,
          })
          await resp.wait()
        }
        return
      }

      throw new Error('Connect wallet to redeem')
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['positions'] })
      void queryClient.invalidateQueries({ queryKey: ['portfolioValue'] })
      void queryClient.invalidateQueries({ queryKey: ['redeemablePositions'] })
      hapticSuccess()
      toast.success('Winnings claimed')
    },
    onError: (err) => {
      hapticError()
      toast.error(err instanceof Error ? err.message : 'Claim error')
    },
  })
}
